// Arcade component style constants
// These are extracted to prevent recreation on every render

// Background gradient strings — exported separately so consumers can
// layer the scanline atmosphere ON TOP of the gradient via multi-image
// backgroundImage. Spreading ARCADE_STYLES.TV_FRAME then overriding
// `backgroundImage` with just the scanline REPLACES the gradient and
// the cabinet reads as nearly transparent (scanline alone is mostly
// transparent). Use these strings to compose:
//
//   backgroundImage: `${KANSEI_DEAD.SCANLINE_BG_2}, ${TV_FRAME_BACKGROUND}`
//
// Multi-image `background-image` stacks first-to-last (first on top), so
// the scanline lines sit over the gradient.
export const TV_FRAME_BACKGROUND =
  "linear-gradient(145deg, #2a2a32 0%, #1f1f25 30%, #161620 70%, #0d0d12 100%)";

export const TV_SCREEN_BACKGROUND =
  "radial-gradient(120% 80% at 50% 0%, rgba(62, 97, 250, 0.10) 0%, transparent 55%), linear-gradient(135deg, #0a0a0b 0%, #0e0e12 100%)";

export const ARCADE_STYLES = {
  // Background ray animation
  BACKGROUND_RAYS: {
    backgroundImage: `
      linear-gradient(45deg, transparent 48%, rgba(255, 255, 0, 0.1) 50%, transparent 52%),
      linear-gradient(-45deg, transparent 48%, rgba(255, 255, 0, 0.1) 50%, transparent 52%)
    `,
    backgroundSize: "40px 40px",
    animation: "rays 20s linear infinite",
  },

  // TV Frame — cabinet bezel. Hibernation read: cobalt rim + faint outer
  // halo (planet-edge / CRT glow) instead of generic chrome white.
  TV_FRAME: {
    background: "linear-gradient(145deg, #2a2a32 0%, #1f1f25 30%, #161620 70%, #0d0d12 100%)",
    boxShadow: `
      0 12px 40px rgba(0, 0, 0, 0.45),
      0 4px 12px rgba(0, 0, 0, 0.3),
      inset 0 1px 0 rgba(255, 255, 255, 0.06),
      inset 0 -1px 0 rgba(0, 0, 0, 0.7),
      inset 0 0 0 1px rgba(62, 97, 250, 0.12),
      0 0 28px rgba(62, 97, 250, 0.14)
    `.trim(),
    border: "1px solid rgba(62, 97, 250, 0.22)",
  },

  // TV Screen — deep recess where the moonscape lives. Top edge gets a
  // subtle cobalt moon-glow so the recess reads as lit-from-above, not
  // generic dark.
  TV_SCREEN: {
    background:
      "radial-gradient(120% 80% at 50% 0%, rgba(62, 97, 250, 0.10) 0%, transparent 55%), linear-gradient(135deg, #0a0a0b 0%, #0e0e12 100%)",
    boxShadow: `
      inset 0 5px 25px rgba(0, 0, 0, 0.95),
      inset 0 -5px 25px rgba(0, 0, 0, 0.9),
      inset 5px 0 25px rgba(0, 0, 0, 0.85),
      inset -5px 0 25px rgba(0, 0, 0, 0.85),
      inset 0 0 50px 10px rgba(0, 0, 0, 0.7),
      inset 0 1px 0 rgba(62, 97, 250, 0.18),
      inset 0 -1px 0 rgba(62, 97, 250, 0.22),
      0 1px 0 rgba(255, 255, 255, 0.03),
      0 0 24px rgba(62, 97, 250, 0.18)
    `.trim(),
    border: "1px solid rgba(62, 97, 250, 0.22)",
    minHeight: "520px",
  },

  // Marquee/Ticker — same cobalt vocabulary as the bezel, slightly cooler
  // so the ticker reads as the room's PA system, not chrome.
  TICKER_CONTAINER: {
    background: "linear-gradient(135deg, #08080a 0%, #11111a 100%)",
    boxShadow: `
      inset 0 2px 10px rgba(0, 0, 0, 0.8),
      inset 0 -1px 0 rgba(62, 97, 250, 0.18),
      0 1px 0 rgba(255, 255, 255, 0.04)
    `.trim(),
    border: "1px solid rgba(62, 97, 250, 0.18)",
  },

  // Live indicator styles
  LIVE_INDICATOR: {
    left: {
      background: "rgba(10, 10, 10, 0.95)",
      borderRight: "2px solid rgba(255, 0, 0, 0.5)",
      boxShadow: "4px 0 12px rgba(0, 0, 0, 0.8), inset -1px 0 0 rgba(255, 0, 0, 0.2)",
      minWidth: "60px",
    },
    right: {
      background: "rgba(10, 10, 10, 0.95)",
      borderLeft: "2px solid rgba(255, 0, 0, 0.5)",
      boxShadow: "-4px 0 12px rgba(0, 0, 0, 0.8), inset 1px 0 0 rgba(255, 0, 0, 0.2)",
      minWidth: "60px",
    },
  },

  // Stats card styles
  STATS_CARD: {
    base: {
      background: "linear-gradient(135deg, #0a0a0b 0%, #141416 100%)",
      boxShadow: `
        inset 0 2px 10px rgba(0, 0, 0, 0.8),
        0 1px 0 rgba(255, 255, 255, 0.05)
      `.trim(),
      border: "1px solid rgba(0, 0, 0, 0.8)",
    },
    hover: {
      apr: {
        background: "linear-gradient(135deg, #0a0a0b 0%, #181822 100%)",
        boxShadow: `
          inset 0 2px 10px rgba(0, 0, 0, 0.8),
          0 1px 0 rgba(255, 255, 255, 0.05),
          0 0 20px rgba(74, 222, 128, 0.1),
          inset 0 0 30px rgba(74, 222, 128, 0.05)
        `.trim(),
      },
      locked: {
        background: "linear-gradient(135deg, #0a0a0b 0%, #161a22 100%)",
        boxShadow: `
          inset 0 2px 10px rgba(0, 0, 0, 0.8),
          0 1px 0 rgba(255, 255, 255, 0.05),
          0 0 20px rgba(96, 165, 250, 0.1),
          inset 0 0 30px rgba(96, 165, 250, 0.05)
        `.trim(),
      },
      burned: {
        background: "linear-gradient(135deg, #0a0a0b 0%, #221614 100%)",
        boxShadow: `
          inset 0 2px 10px rgba(0, 0, 0, 0.8),
          0 1px 0 rgba(255, 255, 255, 0.05),
          0 0 20px rgba(248, 113, 113, 0.1),
          inset 0 0 30px rgba(248, 113, 113, 0.05)
        `.trim(),
      },
      holders: {
        background: "linear-gradient(135deg, #0a0a0b 0%, #1e1622 100%)",
        boxShadow: `
          inset 0 2px 10px rgba(0, 0, 0, 0.8),
          0 1px 0 rgba(255, 255, 255, 0.05),
          0 0 20px rgba(168, 85, 247, 0.1),
          inset 0 0 30px rgba(168, 85, 247, 0.05)
        `.trim(),
      },
    },
  },

  // Section header — hibernation chrome (dark moondust + cobalt rim).
  SECTION_HEADER: {
    background: "linear-gradient(145deg, #2a2a32 0%, #1f1f25 30%, #161620 70%, #0d0d12 100%)",
    boxShadow: `
      0 -2px 8px rgba(0, 0, 0, 0.35),
      inset 0 1px 0 rgba(255, 255, 255, 0.04),
      inset 0 -1px 0 rgba(0, 0, 0, 0.7),
      0 0 0 1px rgba(62, 97, 250, 0.14)
    `.trim(),
    borderTop: "1px solid rgba(62, 97, 250, 0.18)",
    borderLeft: "1px solid rgba(62, 97, 250, 0.14)",
    borderRight: "1px solid rgba(62, 97, 250, 0.14)",
  },

  // Section body — paired with SECTION_HEADER.
  SECTION_BODY: {
    background: "linear-gradient(145deg, #2a2a32 0%, #1f1f25 30%, #161620 70%, #0d0d12 100%)",
    boxShadow: `
      0 12px 40px rgba(0, 0, 0, 0.45),
      0 4px 12px rgba(0, 0, 0, 0.3),
      inset 0 1px 0 rgba(255, 255, 255, 0.04),
      inset 0 -1px 0 rgba(0, 0, 0, 0.7),
      0 0 0 1px rgba(62, 97, 250, 0.14)
    `.trim(),
    borderBottom: "1px solid rgba(62, 97, 250, 0.18)",
    borderLeft: "1px solid rgba(62, 97, 250, 0.14)",
    borderRight: "1px solid rgba(62, 97, 250, 0.14)",
    borderTop: "none",
    marginTop: "-1px",
  },

  // Price chart card styles
  PRICE_CHART: {
    base: {
      background: "linear-gradient(135deg, #0a0a0b 0%, #141416 100%)",
      boxShadow: `
        inset 0 2px 10px rgba(0, 0, 0, 0.8),
        0 1px 0 rgba(255, 255, 255, 0.05)
      `.trim(),
      border: "1px solid rgba(0, 0, 0, 0.8)",
    },
    hover: {
      background: "linear-gradient(135deg, #0a0a0b 0%, #161d22 100%)",
      boxShadow: `
        inset 0 2px 10px rgba(0, 0, 0, 0.8),
        0 1px 0 rgba(255, 255, 255, 0.05),
        0 0 15px rgba(59, 130, 246, 0.1),
        inset 0 0 30px rgba(59, 130, 246, 0.05)
      `.trim(),
    },
  },

  // Tetris container styles - Deep arcade CRT recession
  TETRIS_CONTAINER: {
    background: "rgba(0, 0, 0, 0.95)",
    border: "2px solid rgba(0, 0, 0, 0.8)",
    boxShadow: `
      inset 0 0 50px 15px rgba(0, 0, 0, 0.8),
      inset 0 0 30px 10px rgba(0, 0, 0, 0.6),
      inset 0 8px 20px rgba(0, 0, 0, 0.7),
      inset 0 -8px 20px rgba(0, 0, 0, 0.7),
      inset 8px 0 20px rgba(0, 0, 0, 0.6),
      inset -8px 0 20px rgba(0, 0, 0, 0.6),
      inset 0 2px 4px rgba(0, 0, 0, 0.9),
      inset 0 -2px 4px rgba(0, 0, 0, 0.7),
      0 0 20px rgba(62, 97, 250, 0.2),
      0 1px 0 rgba(255, 255, 255, 0.03)
    `.trim(),
  },
} as const;

// Animation constants for GPU acceleration
export const ANIMATION_CONFIGS = {
  // Icon floating animation
  ICON_FLOAT: {
    x: [0, -0.8, 0.6, 0],
    filter: [
      "drop-shadow(0 0 4px rgba(74, 222, 128, 0.4))",
      "drop-shadow(2px 0 0 rgba(255, 0, 0, 0.15)) drop-shadow(-2px 0 0 rgba(0, 255, 255, 0.15)) drop-shadow(0 0 4px rgba(74, 222, 128, 0.4))",
      "drop-shadow(0 0 4px rgba(74, 222, 128, 0.4))",
    ],
  },

  // Visualizer bar animation
  VISUALIZER: {
    keyframes: `
      @keyframes barScale {
        0%, 100% { transform: scaleY(0.3); }
        20% { transform: scaleY(0.6); }
        40% { transform: scaleY(1); }
        60% { transform: scaleY(0.8); }
        80% { transform: scaleY(0.5); }
      }
    `,
    durations: [0.8, 1.1, 0.9, 1.0, 0.95, 1.05, 0.85, 1.15, 0.9, 1.0, 0.8],
  },

  // Floating text animation
  FLOATING_TEXT: {
    opacity: [0, 1, 0.8, 0],
    scale: [0.5, 1.2, 0.9, 0.7],
    duration: 0.8,
    times: [0, 0.2, 0.6, 1],
  },

  // Live ticker animation
  TICKER: {
    duration: 8,
    distance: "-33.33%", // Move 1/3 for 3 copies
  },

  // Bear animation
  BEAR_HOVER: {
    scale: 1.05,
    transition: {
      type: "spring" as const,
      bounce: 0.3,
      duration: 0.2,
    },
  },

  BEAR_TAP: {
    scale: 0.98,
    y: 2,
    transition: {
      type: "spring" as const,
      bounce: 0,
      duration: 0.1,
    },
  },
} as const;

// Hibernation Poku filter — two-tier treatment.
//
// DEFAULT: "lights down" — Poku is still recognizable, just muted. For
// ambient surfaces (navbar peeking-bear, app cards, music player,
// price-chart-card rotation). Pairs grayscale + brightness reduction
// with cobalt rim drop-shadow.
//
// STRONG: "wound down / basically dead" — Poku reads as truly disabled.
// For surfaces where the room is closed and the operator wants the user
// to feel it (DisabledCabinet astronaut, brick-by-brick when it sits in
// a disabled cabinet, navbar henlo_logo when hibernating). Stronger
// desaturation, lower brightness, slight contrast reduction.
//
// Two tokens, used everywhere. Adding new per-surface filters is a
// register violation — pick the tier that fits or extend these.
export const HIBERNATION_POKU_FILTER = `
  grayscale(0.85)
  brightness(0.62)
  contrast(0.92)
  drop-shadow(0 2px 4px rgba(0, 0, 0, 0.55))
  drop-shadow(0 0 8px rgba(94, 123, 250, 0.16))
`.trim();

export const HIBERNATION_POKU_FILTER_STRONG = `
  grayscale(0.98)
  brightness(0.45)
  contrast(0.9)
  drop-shadow(0 2px 6px rgba(0, 0, 0, 0.7))
  drop-shadow(0 0 10px rgba(94, 123, 250, 0.10))
`.trim();

// LIVING: "barely alive" — heavily drained color, dim brightness. Pairs
// with the .kansei-flicker-living-* register (lit baseline ~0.92 with
// brief stepped struggle). For surfaces that are alive within the AFTER
// HOURS context — navbar fan-out characters, lobby cards for non-locked
// apps, music-player album art when the track is playing.
//
// Tuning notes (operator session-4):
//   v1: grayscale(0.35) brightness(0.92) — too much color, read as
//       "alive in a normal room" rather than "barely alive in a dead
//       lobby." Operator: "too much color on side here."
//   v2: grayscale(0.7) brightness(0.72) — drained, barely alive. The
//       flicker register handles the breath; the filter handles the
//       desaturation. Still distinguishable from STRONG (which is
//       greyscale 0.98 + brightness 0.45 — fully wound down).
export const HIBERNATION_POKU_FILTER_LIVING = `
  grayscale(0.7)
  brightness(0.72)
  contrast(0.94)
  drop-shadow(0 2px 4px rgba(0, 0, 0, 0.6))
  drop-shadow(0 0 8px rgba(94, 123, 250, 0.18))
`.trim();

// ─────────────────────────────────────────────────────────────────────────
// KANSEI / DEAD MOTION + LIGHTING SCHEMA
//
// Persona: YANAGI. "True beauty is not made. It is born."
// Lineage: ported in spirit from sprawl-ui's foundations/motion + aspects/
// register (the `freeside` register — stillness, CRT dampened) +
// stat-card scanline atmosphere.
//
// CORE RULE: institutional surfaces do NOT interpolate smoothly. Motion
// snaps, steps, and flickers — it reads as signal, not ornament.
// Hibernation surfaces lean further: stepped easings, minimal glow, no
// breathing animations that imply ongoing life.
//
// Use these tokens everywhere a hibernation surface needs motion or
// lighting. Custom inline values are register violations.
// ─────────────────────────────────────────────────────────────────────────

export const KANSEI_DEAD = {
  // Stepped easings — quantum flicker over smooth interpolation.
  EASING: {
    SNAP: "steps(1)",         // instant binary on/off (no transition)
    QUANTUM: "steps(4)",      // canonical "feels mechanical"
    QUANTUM_SOFT: "steps(8)", // slightly smoother stepped motion
    LINEAR: "linear",         // continuous mechanical (ticker scrolls only)
  },

  // Quantum-aligned durations. Multiples of perceptual cycle (100ms).
  // 83ms = baseline tick (sub-perceptual; institutional micro-feedback).
  DURATION: {
    QUANTUM: "83ms",
    FAST: "100ms",
    NORMAL: "200ms",
    SLOW: "400ms",
    DELIBERATE: "800ms",
  },

  // Glow ladder. Stillness register caps at 2px — anything bigger reads
  // as "alive." For henlo's AFTER HOURS, prefer NONE or WHISPER.
  GLOW: {
    NONE: "none",
    WHISPER: "0 0 1px rgba(94, 123, 250, 0.3)",
    DIM: "0 0 2px rgba(94, 123, 250, 0.4)",
  },

  // Scanline atmosphere — six tiers of CRT residue. Apply via
  // backgroundImage on hibernation surfaces so the surface reads as
  // "powered down CRT," not "lit CRT." Ported from sprawl-ui
  // stat-card data-tile-intensity.
  SCANLINE_ALPHA: {
    0: 0.01,
    1: 0.015,
    2: 0.025, // baseline for henlo hibernation surfaces
    3: 0.035,
    4: 0.05,
    5: 0.065,
  },

  // Composed scanline backgroundImage at intensity 2 (henlo default).
  SCANLINE_BG_2:
    "repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255, 255, 255, 0.025) 2px, rgba(255, 255, 255, 0.025) 4px)",

  // Stepped transitions — drop-in for any hibernation surface needing
  // hover/state feedback. Composes via shorthand: just spread it.
  TRANSITION: {
    BORDER_QUANTUM: "border-color 166ms steps(4)",
    SHADOW_QUANTUM: "box-shadow 166ms steps(4)",
    OPACITY_QUANTUM: "opacity 166ms steps(4)",
    TRANSFORM_QUANTUM: "transform 200ms steps(4)",
  },

  // Icon ghost color — outline-only, no fill, no chromatic glow.
  // Replaces the chromatic per-card halos (green/yellow/red/purple) with
  // a single moondust ghost.
  ICON_GHOST_COLOR: "#71717A", // zinc-500
} as const;

// Strike colors for consistency
export const STRIKE_COLORS = [
  "#808080",
  "#964DFF",
  "#4578FF",
  "#1CC75E",
  "#F3C734",
  "#D51A35",
] as const;

// Font options for floating texts
export const FLOATING_TEXT_FONTS = ["font-pressStart2P", "font-clash", "font-mono"] as const;
